import { useEffect, useRef, useState } from 'react';

/**
 * Default debounce delay. The modal value across the repo's search/filter
 * call sites; pass an explicit `delay` where a surface needs different tuning.
 */
export const DEFAULT_DEBOUNCE_MS = 200;

export type DebounceOptions = {
  /**
   * If true, fire an update on the leading edge (immediately when value changes
   * after a quiet period). Defaults to false.
   */
  leading?: boolean;
  /**
   * If true, fire an update on the trailing edge (after `delay` ms of no
   * further activity). Defaults to true.
   */
  trailing?: boolean;
};

/**
 * Debounces a value, returning the most recently committed value subject to
 * the configured leading/trailing edge behavior.
 *
 * Defaults match the historical `useDebouncedString` behavior: trailing-only,
 * which means the returned value updates `delay` ms after the input stops
 * changing.
 *
 * Pass `{ leading: true }` to fire on the leading edge instead of (or in
 * addition to, with `{ leading: true, trailing: true }`) the trailing edge.
 */
export function useDebouncedValue<T>(value: T, delay: number = DEFAULT_DEBOUNCE_MS, options?: DebounceOptions): T {
  const leading = options?.leading ?? false;
  const trailing = options?.trailing ?? true;

  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  // A cooldown is active between a leading-edge fire and `delay` ms later. While
  // active, additional value changes do NOT fire on the leading edge again; they
  // either wait for the trailing edge (if enabled) or are dropped.
  const cooldownTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Tracks the latest value observed during the cooldown — used to emit on the
  // trailing edge.
  const latestValueRef = useRef<T>(value);
  // True if at least one value change has occurred during the current cooldown
  // window.
  const hasTrailingPendingRef = useRef<boolean>(false);

  useEffect(() => {
    latestValueRef.current = value;

    if (leading) {
      if (cooldownTimerRef.current === undefined) {
        // Quiet period — fire leading edge immediately and open a cooldown.
        setDebouncedValue(value);
        hasTrailingPendingRef.current = false;
        cooldownTimerRef.current = setTimeout(() => {
          if (trailing && hasTrailingPendingRef.current) {
            setDebouncedValue(latestValueRef.current);
          }
          cooldownTimerRef.current = undefined;
          hasTrailingPendingRef.current = false;
        }, delay);
      } else {
        // In cooldown — mark a trailing update as pending.
        hasTrailingPendingRef.current = true;
      }
      return;
    }

    // Trailing-only path — restart timer on each value change.
    if (cooldownTimerRef.current !== undefined) {
      clearTimeout(cooldownTimerRef.current);
    }
    cooldownTimerRef.current = setTimeout(() => {
      if (trailing) {
        setDebouncedValue(latestValueRef.current);
      }
      cooldownTimerRef.current = undefined;
    }, delay);
  }, [value, delay, leading, trailing]);

  // Clear pending timer on unmount only.
  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current !== undefined) {
        clearTimeout(cooldownTimerRef.current);
        cooldownTimerRef.current = undefined;
      }
    };
  }, []);

  return debouncedValue;
}
